import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { HelpCircle, Bug, MessageSquare, Lightbulb, Send, Loader2, CheckCircle } from 'lucide-react';

const REPORT_TYPES = [
  { value: 'bug', label: 'Bug Report', icon: Bug, color: 'text-red-500' },
  { value: 'support', label: 'Support Request', icon: HelpCircle, color: 'text-blue-500' },
  { value: 'feedback', label: 'Feedback', icon: MessageSquare, color: 'text-green-500' },
  { value: 'feature', label: 'Feature Request', icon: Lightbulb, color: 'text-amber-500' },
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low — Minor inconvenience' },
  { value: 'medium', label: 'Medium — Impacts workflow' },
  { value: 'high', label: 'High — Blocking work' },
  { value: 'critical', label: 'Critical — System down' },
];

export default function HelpReportDialog({ open, onOpenChange, portal, user, apiClient, apiPrefix = '/api' }) {
  const [type, setType] = useState('bug');
  const [form, setForm] = useState({
    subject: '', description: '', steps_to_reproduce: '',
    expected_behavior: '', actual_behavior: '',
    severity: 'medium', additional_info: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reportId, setReportId] = useState('');

  const reset = () => {
    setType('bug');
    setForm({ subject: '', description: '', steps_to_reproduce: '', expected_behavior: '', actual_behavior: '', severity: 'medium', additional_info: '' });
    setSubmitted(false);
    setReportId('');
  };

  const handleClose = (val) => {
    if (!val) reset();
    onOpenChange(val);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim()) { toast.error('Subject is required'); return; }
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form, type, portal,
        page_url: window.location.href,
        browser_info: navigator.userAgent,
        submitted_by: user?.name || user?.full_name || '',
        submitted_by_email: user?.email || '',
        submitted_by_role: user?.role || portal,
      };
      const res = await apiClient.post(`${apiPrefix}/help/report`, payload);
      setReportId(res.data.report_id);
      setSubmitted(true);
      toast.success('Report submitted!');
    } catch {
      toast.error('Failed to submit report');
    } finally { setSubmitting(false); }
  };

  const selectedType = REPORT_TYPES.find(t => t.value === type);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto" data-testid="help-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <HelpCircle className="w-5 h-5 text-primary" /> Help & Support
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="flex flex-col items-center py-8 gap-4" data-testid="help-report-success">
            <CheckCircle className="w-14 h-14 text-green-500" />
            <h3 className="text-lg font-semibold">Report Submitted</h3>
            <p className="text-sm text-muted-foreground text-center">
              Your {selectedType?.label?.toLowerCase()} has been received.<br />
              Reference: <span className="font-mono font-bold">{reportId}</span>
            </p>
            <p className="text-xs text-muted-foreground">Our team will review and respond shortly.</p>
            <Button onClick={() => handleClose(false)} className="mt-2" data-testid="help-report-close-btn">Close</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Type Selection */}
            <div className="grid grid-cols-4 gap-2" data-testid="help-report-type-selector">
              {REPORT_TYPES.map(t => (
                <button key={t.value} type="button" onClick={() => setType(t.value)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-xs font-medium transition-all ${type === t.value ? 'border-primary bg-primary/5 shadow-sm' : 'border-muted hover:border-primary/30'}`}
                  data-testid={`help-type-${t.value}`}
                >
                  <t.icon className={`w-5 h-5 ${t.color}`} />
                  {t.label}
                </button>
              ))}
            </div>

            <div>
              <Label>Subject *</Label>
              <Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} placeholder="Brief summary of the issue" required className="mt-1" data-testid="help-subject" />
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Describe what happened in detail..." required rows={3} className="mt-1" data-testid="help-description" />
            </div>

            {(type === 'bug') && (
              <>
                <div>
                  <Label>Steps to Reproduce</Label>
                  <Textarea value={form.steps_to_reproduce} onChange={e => setForm({...form, steps_to_reproduce: e.target.value})} placeholder={"1. Go to...\n2. Click on...\n3. Observe..."} rows={3} className="mt-1" data-testid="help-steps" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Expected Behavior</Label>
                    <Textarea value={form.expected_behavior} onChange={e => setForm({...form, expected_behavior: e.target.value})} placeholder="What should happen?" rows={2} className="mt-1" data-testid="help-expected" />
                  </div>
                  <div>
                    <Label>Actual Behavior</Label>
                    <Textarea value={form.actual_behavior} onChange={e => setForm({...form, actual_behavior: e.target.value})} placeholder="What actually happens?" rows={2} className="mt-1" data-testid="help-actual" />
                  </div>
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Severity</Label>
                <Select value={form.severity} onValueChange={v => setForm({...form, severity: v})}>
                  <SelectTrigger className="mt-1" data-testid="help-severity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITY_OPTIONS.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Page / Section</Label>
                <Input value={window.location.pathname} disabled className="mt-1 text-xs opacity-60" />
              </div>
            </div>

            <div>
              <Label>Additional Information (optional)</Label>
              <Textarea value={form.additional_info} onChange={e => setForm({...form, additional_info: e.target.value})} placeholder="Any other context, screenshots description, error messages..." rows={2} className="mt-1" data-testid="help-additional" />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="help-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                {submitting ? 'Submitting...' : 'Submit Report'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
